/**
 * Sidebar Component
 *
 * Main navigation sidebar with role-based menu filtering.
 */

import { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronLeft, ChevronRight, LogOut } from 'lucide-react';

import { Button } from '@/components/common/Button';
import { useAuth } from '@/hooks';
import { usePermissionStore } from '@/store/slices/permissionSlice';
import { cn } from '@/utils/cn';
import { MENU_ITEMS, USER_MENU_ITEMS, type MenuItem } from '@/config/menu.config';
import { APP_INFO } from '@/config/app.config';

interface SidebarProps {
  /** Whether sidebar is expanded */
  expanded: boolean;
  /** Toggle sidebar expansion */
  onToggle: () => void;
  /** Whether sidebar is open on mobile */
  mobileOpen: boolean;
  /** Close mobile sidebar */
  onMobileClose: () => void;
}

export function Sidebar({ expanded, onToggle, mobileOpen, onMobileClose }: SidebarProps) {
  const { logout } = useAuth();

  // Dynamic RBAC menu gating — the single source of truth for menu visibility.
  // Items carry a `permKey`; an item without one is always shown (basic items
  // like Dashboard/Profile). FAIL OPEN until permissions load (a slow/failed
  // permissions call must not blank the nav); items marked failClosed appear
  // only after an explicit grant.
  const permLoaded = usePermissionStore((s) => s.loaded);
  const permIsOwner = usePermissionStore((s) => s.isOwner);
  const permissions = usePermissionStore((s) => s.permissions);

  useEffect(() => {
    void usePermissionStore.getState().load();
  }, []);

  const applyMenuGating = (items: MenuItem[]) =>
    items.filter((item) => {
      if (!item.permKey) return true;
      const allowed = permIsOwner || permissions.has(item.permKey);
      if (item.failClosed) return permLoaded && allowed;
      return !permLoaded || allowed;
    });

  const mainMenuItems = applyMenuGating(MENU_ITEMS);
  const userMenuItems = applyMenuGating(USER_MENU_ITEMS);

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col',
          'bg-card dark:bg-card-dark',
          'border-r border-border dark:border-border-dark',
          'transition-all duration-300 ease-in-out',
          // Desktop
          'lg:relative lg:translate-x-0',
          // Mobile
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          // Width
          expanded ? 'w-64' : 'w-20'
        )}
      >
        {/* Header */}
        <div className="flex h-16 items-center justify-between px-4 border-b border-border dark:border-border-dark">
          {expanded ? (
            <span className="text-xl font-bold text-primary truncate">
              {APP_INFO.name.split(' ')[0]}
            </span>
          ) : (
            <span className="text-xl font-bold text-primary mx-auto">CP</span>
          )}

          {/* Desktop toggle button */}
          <button
            onClick={onToggle}
            className={cn(
              'hidden lg:flex items-center justify-center',
              'h-8 w-8 rounded-md',
              'hover:bg-secondary-100 dark:hover:bg-secondary-800',
              'text-foreground-muted',
              !expanded && 'mx-auto'
            )}
            title={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {expanded ? (
              <ChevronLeft className="h-5 w-5" />
            ) : (
              <ChevronRight className="h-5 w-5" />
            )}
          </button>
        </div>

        {/* Main navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {expanded && (
            <p className="px-3 mb-2 text-xs font-semibold text-foreground-muted dark:text-foreground-dark-muted uppercase tracking-wider">
              Menu
            </p>
          )}
          <ul className="space-y-1">
            {mainMenuItems.map((item) => (
              <SidebarItem
                key={item.key}
                item={item}
                expanded={expanded}
                onClick={onMobileClose}
              />
            ))}
          </ul>
        </nav>

        {/* User section */}
        <div className="border-t border-border dark:border-border-dark py-4 px-3">
          {expanded && (
            <p className="px-3 mb-2 text-xs font-semibold text-foreground-muted dark:text-foreground-dark-muted uppercase tracking-wider">
              Account
            </p>
          )}
          <ul className="space-y-1">
            {userMenuItems.map((item) => (
              <SidebarItem
                key={item.key}
                item={item}
                expanded={expanded}
                onClick={onMobileClose}
              />
            ))}
          </ul>

          {/* Logout button */}
          <div className="mt-2 px-1">
            <Button
              variant="ghost"
              size={expanded ? 'md' : 'icon'}
              className={cn(
                'w-full text-foreground-muted hover:text-danger hover:bg-danger-50 dark:hover:bg-danger-900/20',
                !expanded && 'justify-center'
              )}
              onClick={logout}
              title="Logout"
            >
              <LogOut className="h-5 w-5" />
              {expanded && <span className="ml-3">Logout</span>}
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
}

/**
 * Individual sidebar menu item
 */
interface SidebarItemProps {
  item: MenuItem;
  expanded: boolean;
  onClick?: () => void;
}

function SidebarItem({ item, expanded, onClick }: SidebarItemProps) {
  const Icon = item.icon;

  return (
    <li>
      <NavLink
        to={item.path}
        onClick={onClick}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 px-3 py-2.5 rounded-lg',
            'text-sm font-medium',
            'transition-colors duration-200',
            !expanded && 'justify-center',
            isActive
              ? 'bg-primary-50 text-primary dark:bg-primary-900/20 dark:text-primary-400'
              : 'text-foreground-muted hover:bg-secondary-100 hover:text-foreground dark:text-foreground-dark-muted dark:hover:bg-secondary-800 dark:hover:text-foreground-dark'
          )
        }
        title={!expanded ? item.label : undefined}
      >
        <Icon className="h-5 w-5 shrink-0" />
        {expanded && <span className="truncate">{item.label}</span>}
      </NavLink>
    </li>
  );
}
